//mushroom-materials-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  useGridApiRef,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingBackdrop from "../components/loading-backdrop";
import Page from "../components/page";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { useHistory } from "react-router-dom";

type MushroomMaterialCategory =
  | "grain"
  | "substrate"
  | "supplement"
  | "container"
  | "other";

interface MushroomMaterial {
  id: number;
  name: string;
  category: MushroomMaterialCategory;
  unit: string | null;
  notes: string | null;
  archivedAt: string | null;
}

const materialsGridStateStorageKey = "mushroomMaterialsGridState";
const materialsGridDensityStorageKey = "mushroomMaterialsGridDensity";

const categoryLabel = (c: MushroomMaterialCategory) => {
  switch (c) {
    case "grain":
      return "Grain";
    case "substrate":
      return "Substrate";
    case "supplement":
      return "Supplement";
    case "container":
      return "Container";
    case "other":
      return "Other";
    default:
      return c;
  }
};

const MaterialsDataGrid = React.memo(
  (props: {
    materials: MushroomMaterial[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const csvFileName = React.useMemo(() => {
      const now = DateTime.local();

      return `SeedLeaf Mushroom Materials - ${now.toFormat("MMM dd, yyyy")}`;
    }, []);

    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(materialsGridStateStorageKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;
            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }
        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 25 },
          };
        }

        localStorage.setItem(
          materialsGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(materialsGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(materialsGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "name", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        materialsGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "name",
        headerName: "Name",
        width: 280,
      },
      {
        field: "category",
        headerName: "Category",
        width: 180,
        renderCell: (params) => categoryLabel(params.row.category),
      },
      {
        field: "unit",
        headerName: "Unit",
        width: 140,
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 360,
      },
    ];

    if (!props.materials) return null;

    if (!initialState) {
      return <CircularProgress />;
    }

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.materials.map((m) => ({
          id: m.id,
          name: m.name,
          category: m.category,
          unit: m.unit,
          notes: m.notes,
          archivedAt: m.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(materialsGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

export default function MushroomMaterialsPage() {
  const [materials, setMaterials] = useState<MushroomMaterial[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const showArchivedCheckedStorageKey = "mushroomMaterialsShowArchivedChecked";
  const showActiveCheckedStorageKey = "mushroomMaterialsShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addCategory, setAddCategory] =
    useState<MushroomMaterialCategory>("substrate");
  const [addUnit, setAddUnit] = useState("");
  const [addNotes, setAddNotes] = useState("");

  const history = useHistory();
  let initialPage = getSearchParamNumber(history, "page", 0);

  useEffect(() => {
    setMaterials([
      {
        id: 1,
        name: "Hardwood pellets",
        category: "substrate",
        unit: "lb",
        notes: null,
        archivedAt: null,
      },
      {
        id: 2,
        name: "Soy hulls",
        category: "supplement",
        unit: "lb",
        notes: null,
        archivedAt: null,
      },
      {
        id: 3,
        name: "Mushroom grow bag",
        category: "container",
        unit: "each",
        notes: null,
        archivedAt: null,
      },
    ]);
  }, []);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    // Placeholder: later we can route to /mushroom/home/materials/:id
    return;
  };

  const handleClickAdd = () => {
    setAddName("");
    setAddCategory("substrate");
    setAddUnit("");
    setAddNotes("");
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!materials) return;

    if (!addName.trim()) {
      return;
    }

    setBusy(true);

    try {
      const maxId = materials.reduce((m, r) => Math.max(m, r.id), 0);

      const next: MushroomMaterial = {
        id: maxId + 1,
        name: addName.trim(),
        category: addCategory,
        unit: addUnit.trim() ? addUnit.trim() : null,
        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,
      };

      setMaterials([next, ...materials]);
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="Materials">
      {materials && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickAdd}
              disabled={busy}
            >
              Add
            </Button>

            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={() => {
                return;
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <MaterialsDataGrid
              materials={materials.filter(
                (material) =>
                  (showActiveChecked && material.archivedAt === null) ||
                  (showArchivedChecked && material.archivedAt !== null),
              )}
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({
                  search: params.toString(),
                });
              }}
              onClickRow={onClickRow}
            />
          </Box>

          <Dialog
            open={addOpen}
            onClose={handleCloseAdd}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Add Material</DialogTitle>
            <DialogContent>
              <Box
                sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  label="Name"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Category"
                  value={addCategory}
                  onChange={(e) =>
                    setAddCategory(e.target.value as MushroomMaterialCategory)
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="grain">Grain</MenuItem>
                  <MenuItem value="substrate">Substrate</MenuItem>
                  <MenuItem value="supplement">Supplement</MenuItem>
                  <MenuItem value="container">Container</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
                <TextField
                  label="Unit"
                  value={addUnit}
                  onChange={(e) => setAddUnit(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Notes"
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdd} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAdd}
                color="primary"
                variant="contained"
                disabled={busy || !addName.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
